import { useEffect, useState } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Plus, Edit, Eye, Send, Calendar, Users, CheckCircle, XCircle, Trash, RefreshCw } from 'lucide-react';
import CampaignEditor from './CampaignEditor';

interface TargetFilters {
  status?: string | null;
  source?: string | null;
  specific_emails?: string[];
}

interface Campaign {
  id: string;
  name: string;
  description: string | null;
  target_table?: string;
  target_filters?: TargetFilters;
  target_type?: string;
  subject: string;
  html_body: string;
  brand: string;
  status: 'draft' | 'queued' | 'sent';
  queued_at: string | null;
  sent_at: string | null;
  last_sent_at: string | null;
  created_at: string;
  updated_at: string;
  emails_sent?: number;
  emails_failed?: number;
  emails_total?: number;
}

interface CampaignStats {
  total: number;
  sent: number;
  failed: number;
}

export default function CampaignsManager() {
  const { getAdminToken } = useAdminAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-admin`,
        {
          headers: {
            'X-Admin-Token': token,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load campaigns');
      }

      const data = await response.json();
      setCampaigns(Array.isArray(data) ? data : []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    await loadCampaigns(true);
  };

  const handleNewCampaign = () => {
    setEditingId('new');
    setShowEditor(true);
  };

  const handleEdit = (campaign: Campaign) => {
    setEditingId(campaign.id);
    setShowEditor(true);
  };

  const handleView = (campaign: Campaign) => {
    setEditingId(campaign.id);
    setShowEditor(true);
  };

  const handleQueueCampaign = async (campaignId: string) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) return;

    const targetInfo = getTargetInfo(campaign);
    const confirmMessage = `Are you sure you want to queue this campaign for sending?\n\nCampaign: ${campaign.name}\nTarget: ${targetInfo}`;

    if (!confirm(confirmMessage)) return;

    try {
      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-queue`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Token': token,
          },
          body: JSON.stringify({ campaign_id: campaignId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to queue campaign');
      }

      const result = await response.json();
      alert(`Campaign queued successfully! ${result.queued_recipients} emails will be sent.`);

      await loadCampaigns();
    } catch (error) {
      console.error('Error queuing campaign:', error);
      alert(`Failed to queue campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDelete = async (campaignId: string, campaignName: string) => {
    const confirmMessage = `Are you sure you want to delete this campaign?\n\nCampaign: ${campaignName}\n\nThis will permanently delete the campaign and all related data.`;

    if (!confirm(confirmMessage)) return;

    try {
      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-admin/${campaignId}`,
        {
          method: 'DELETE',
          headers: {
            'X-Admin-Token': token,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete campaign');
      }

      alert('Campaign deleted successfully!');
      await loadCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert(`Failed to delete campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleResend = async (campaignId: string, campaignName: string) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) return;

    const targetInfo = getTargetInfo(campaign);
    const confirmMessage = `Are you sure you want to resend this campaign?\n\nCampaign: ${campaignName}\nTarget: ${targetInfo}\n\nThis will queue the campaign for sending again to all recipients.`;

    if (!confirm(confirmMessage)) return;

    try {
      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-queue`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Token': token,
          },
          body: JSON.stringify({ campaign_id: campaignId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to resend campaign');
      }

      const result = await response.json();
      alert(`Campaign queued for resending! ${result.queued_recipients} emails will be sent.`);

      await loadCampaigns();
    } catch (error) {
      console.error('Error resending campaign:', error);
      alert(`Failed to resend campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleClose = () => {
    setShowEditor(false);
    setEditingId(null);
    loadCampaigns();
  };

  const getTargetInfo = (campaign: Campaign) => {
    const table = campaign.target_table || campaign.target_type;
    const filters = campaign.target_filters;

    let baseLabel = '';
    if (table === 'game_dev_applications' || table === 'advance_applicants') {
      baseLabel = 'Game Developer Applicants';
    } else if (table === 'email_list' || table === 'email_list_all') {
      baseLabel = 'Newsletter Subscribers';
    } else {
      baseLabel = table || 'Unknown';
    }

    if (!filters) return baseLabel;

    const filterParts = [];
    if (filters.status) {
      filterParts.push(`Status: ${filters.status}`);
    }
    if (filters.source) {
      filterParts.push(`Source: ${filters.source}`);
    }
    if (filters.specific_emails && filters.specific_emails.length > 0) {
      filterParts.push(`${filters.specific_emails.length} specific recipients`);
    }

    if (filterParts.length > 0) {
      return `${baseLabel} (${filterParts.join(', ')})`;
    }

    return baseLabel;
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
      queued: 'bg-yellow-400/20 text-yellow-400 border-yellow-400/30',
      sent: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return badges[status as keyof typeof badges] || badges.draft;
  };

  if (showEditor) {
    return (
      <CampaignEditor
        campaignId={editingId === 'new' ? null : editingId}
        onClose={handleClose}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-400 text-lg">Loading campaigns...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-yellow-400 mb-2">Email Campaigns</h2>
          <p className="text-gray-400">Create and send bulk updates to applicants and subscribers</p>
          <p className="text-gray-500 text-sm mt-1">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh campaigns and stats"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={handleNewCampaign}
            className="flex items-center gap-2 px-6 py-3 bg-yellow-400 text-black rounded-lg hover:bg-yellow-300 transition-colors font-bold"
          >
            <Plus size={20} />
            New Campaign
          </button>
        </div>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">No campaigns yet</div>
          <button
            onClick={handleNewCampaign}
            className="text-yellow-400 hover:text-yellow-300 underline"
          >
            Create your first campaign
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => {
            const hasStats = campaign.emails_total != null && campaign.emails_total > 0;

            return (
              <div
                key={campaign.id}
                className="bg-black/50 border border-yellow-400/30 rounded-lg p-6 hover:border-yellow-400/50 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-white">{campaign.name}</h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusBadge(
                          campaign.status
                        )}`}
                      >
                        {campaign.status.toUpperCase()}
                      </span>
                    </div>
                    {campaign.description && (
                      <p className="text-gray-400 mb-2">{campaign.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {getTargetInfo(campaign)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(campaign.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {campaign.status === 'draft' ? (
                      <>
                        <button
                          onClick={() => handleEdit(campaign)}
                          className="p-2 text-yellow-400 hover:bg-yellow-400/10 rounded-lg transition-colors"
                          title="Edit Campaign"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleQueueCampaign(campaign.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-300 transition-colors font-bold"
                        >
                          <Send size={16} />
                          Queue Send
                        </button>
                        <button
                          onClick={() => handleDelete(campaign.id, campaign.name)}
                          className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                          title="Delete Campaign"
                        >
                          <Trash size={18} />
                        </button>
                      </>
                    ) : campaign.status === 'sent' ? (
                      <>
                        <button
                          onClick={() => handleEdit(campaign)}
                          className="p-2 text-yellow-400 hover:bg-yellow-400/10 rounded-lg transition-colors"
                          title="Edit as New Campaign"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleResend(campaign.id, campaign.name)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-400 transition-colors font-bold"
                          title="Resend Campaign"
                        >
                          <RefreshCw size={16} />
                          Resend
                        </button>
                        <button
                          onClick={() => handleDelete(campaign.id, campaign.name)}
                          className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                          title="Delete Campaign"
                        >
                          <Trash size={18} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleView(campaign)}
                          className="p-2 text-gray-400 hover:bg-gray-400/10 rounded-lg transition-colors"
                          title="View Campaign"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(campaign.id, campaign.name)}
                          className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                          title="Delete Campaign"
                        >
                          <Trash size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {campaign.status !== 'draft' && (
                  <div className="mt-4 pt-4 border-t border-yellow-400/20">
                    {campaign.last_sent_at && (
                      <div className="text-sm text-gray-400 mb-2">
                        Last sent: {new Date(campaign.last_sent_at).toLocaleString()}
                      </div>
                    )}
                    {hasStats ? (
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">Total:</span>
                          <span className="text-white font-bold">{campaign.emails_total}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle size={14} className="text-green-400" />
                          <span className="text-gray-400">Sent:</span>
                          <span className="text-green-400 font-bold">{campaign.emails_sent || 0}</span>
                        </div>
                        {(campaign.emails_failed || 0) > 0 && (
                          <div className="flex items-center gap-2">
                            <XCircle size={14} className="text-red-400" />
                            <span className="text-gray-400">Failed:</span>
                            <span className="text-red-400 font-bold">{campaign.emails_failed}</span>
                          </div>
                        )}
                        {campaign.emails_total && campaign.emails_total > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400">Delivery Rate:</span>
                            <span className="text-yellow-400 font-bold">
                              {Math.round(((campaign.emails_sent || 0) / campaign.emails_total) * 100)}%
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No stats available yet</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
